import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import AiImageGenerationTabTitle from '../../../components/Title'
import { CHARACTERS_ERROR_STATE } from 'common-components/CharacterCountIndicator/utils'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import * as Styled from './styled'
import { IMG_PROMPT_HEIGHT, IMG_PROMPT_MAX_LENGTH } from './consts'
import {
  IMentionPickerOption,
  PromptEditor
} from 'containers/SubSidebar/shared/PromptMentionEditor'
import { useSelector } from 'react-redux'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { dispatch } from 'store'
import debounce from 'lodash.debounce'

const PROMPT_DEBOUNCE_MS = 400

const AiImageGenerationPrompt: React.FC = () => {
  const prompt = useSelector(aiImageSelectors.prompt)
  const refImages = useSelector(aiImageSelectors.refImages)
  // Mirrors the store so typing stays responsive; the store is written
  // debounced below.
  const [currentPrompt, setCurrentPrompt] = React.useState(prompt)
  // What we last pushed to the store, so the sync-down effect can tell an
  // external write (mention renumbering, "Create similar", regeneration form)
  // from the echo of our own debounced dispatch.
  const lastPushedRef = useRef(prompt)

  // Memoized so there is ONE timer to cancel. (Re-creating it per render gave
  // every keystroke its own timer, which would let a stale keystroke land
  // AFTER a store-side mention renumber and resurrect the wrong chip numbers.)
  const debouncedSetPrompt = useMemo(
    () =>
      debounce((textValue: string) => {
        lastPushedRef.current = textValue
        dispatch(aiImageThunks.setPrompt(textValue))
      }, PROMPT_DEBOUNCE_MS),
    []
  )

  useEffect(() => () => debouncedSetPrompt.cancel(), [debouncedSetPrompt])

  const handleChange = useCallback(
    (value: string) => {
      setCurrentPrompt(value)
      debouncedSetPrompt(value)
    },
    [debouncedSetPrompt]
  )

  useEffect(() => {
    if (prompt === lastPushedRef.current) {
      return
    }

    // The store changed from the outside — drop any pending keystroke so it
    // cannot overwrite the new value, then adopt it.
    debouncedSetPrompt.cancel()
    lastPushedRef.current = prompt
    setCurrentPrompt(prompt)
  }, [prompt, debouncedSetPrompt])

  const placeholderTexts = useMemo(
    () => [
      translate(T.PROMPT_PLACEHOLDER_RESTYLE),
      translate(T.PROMPT_PLACEHOLDER_LIGHTING),
      translate(T.PROMPT_PLACEHOLDER_PRODUCT)
    ],
    []
  )

  // Nothing to filter out: refImages only ever holds completed uploads
  // (aiImage/thunks/upload.ts adds items after uploadMedias resolves), so unlike
  // AI Video there are never un-referenceable entries to omit — and therefore no
  // gaps in these numbers.
  const mentionOptions = useMemo<Array<IMentionPickerOption>>(
    () =>
      refImages.map((image, index) => ({
        id: image.id,
        imageNumber: index + 1,
        thumbnailUrl: image.thumbnailUrl || image.url
      })),
    [refImages]
  )

  const currentTextLength = currentPrompt.length
  const isErrorState = currentTextLength >= IMG_PROMPT_MAX_LENGTH
  const textWarningState = isErrorState ? CHARACTERS_ERROR_STATE : undefined

  return (
    <Styled.AiImageGenerationPrompt>
      <AiImageGenerationTabTitle>
        {translate(T.IMAGE_PROMPT_TITLE)}
      </AiImageGenerationTabTitle>
      <PromptEditor
        value={currentPrompt}
        onChange={handleChange}
        placeholderTexts={placeholderTexts}
        minHeight={105}
        maxHeight={IMG_PROMPT_HEIGHT}
        maxLength={IMG_PROMPT_MAX_LENGTH}
        hasError={isErrorState}
        options={mentionOptions}
        data-testid="ai-image-prompt-editor"
      />
      <Styled.CharacterCountIndicatorWrapper
        currentTextLength={currentTextLength}
        maxCount={IMG_PROMPT_MAX_LENGTH}
        textWarningState={textWarningState}
      />
    </Styled.AiImageGenerationPrompt>
  )
}

export default AiImageGenerationPrompt
